import { useEffect } from 'react';

/**
 * useAdSuppression
 *
 * Prevents Google AdSense (in particular Auto Ads injected via the site-wide
 * `adsbygoogle.js` script in `index.html`) from displaying on the current
 * page.
 *
 * Google AdSense policy discourages ads on "no-content" or legal pages such as
 * Privacy Policy, Terms of Service, About, and Contact. Even empty auto-ad
 * placeholders on these pages can trigger a policy rejection.
 *
 * How it works:
 *   1. While this hook is mounted it sets `data-no-ads="true"` on the root
 *      `<html>` element.
 *   2. A companion CSS rule (in `index.css`) hides every `ins.adsbygoogle`
 *      and `.adsbygoogle` element while that attribute is present.
 *   3. On mount and briefly afterwards we also imperatively strip any
 *      adsbygoogle nodes that AdSense may have already injected into the DOM
 *      before React finished mounting the route.
 *
 * The hook is a no-op on the server (SSR safety) and cleans up on unmount so
 * ads resume on non-legal pages.
 */
export function useAdSuppression() {
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const root = document.documentElement;
        root.setAttribute('data-no-ads', 'true');

        const strip = () => {
            document
                .querySelectorAll('ins.adsbygoogle, .adsbygoogle')
                .forEach((el) => {
                    // Remove any auto-injected AdSense containers so they can't
                    // leave a visible empty rectangle on the page.
                    el.parentNode && el.parentNode.removeChild(el);
                });
        };

        // Run once immediately, then a couple of retries because AdSense
        // Auto Ads can inject asynchronously after route change.
        strip();
        const timers = [
            setTimeout(strip, 500),
            setTimeout(strip, 1500),
            setTimeout(strip, 3000),
        ];

        return () => {
            timers.forEach(clearTimeout);
            root.removeAttribute('data-no-ads');
        };
    }, []);
}

export default useAdSuppression;
